/* eslint-disable max-lines */
import AppRouter from 'common/AppRouter';
import {
  IDENTITY_PROVIDERS,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  TRACKING
} from 'common/constants/constants';
import axios from 'common/requester';
import { getAccount } from 'common/services/account.service';
import {
  accountApprovedGdpr,
  isGdprCheckboxChecked
} from 'common/services/gdpr.service';
import { getToken, storeToken } from 'common/services/token.service';
import * as TrackingService from 'common/services/tracking.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { getFirstAndLastNames } from 'common/utils/getFirstAndLastNames';
import get from 'lodash/get';
import QueryString from 'query-string';
import { validateFetchAccountError } from 'state/callers';
import { API_GATEWAY_URI } from 'common/constants/api-urls';
import { omit } from 'lodash';
import { urlStringHandler, isSurveyFlow } from 'common/utils/utils';
import { WITH_MULTI_SITES_ERROR_QS } from 'modals/MultiSitesErrorModal';
import { WITH_SCOPES_VALIDATIONS_QS } from 'views/oauth/scopes-validations/useScopesValidationsModalHandler';

export const cleanSignInContext = (
  sessionStorageAccessor,
  localStorageAccessor
) => {
  // Clean sign in context after oauth

  // SESSION STORAGE
  sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.REGISTRATION_TOKEN);
  sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.AUTH_FLOW);
  sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.INVITATION_ID);
  sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.ORG_ID);

  // LOCAL STORAGE
  localStorageAccessor.remove(LOCAL_STORAGE_KEYS.SSO_LOGIN_EMAIL);
  localStorageAccessor.remove(LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY);
};

export const getError = err =>
  JSON.parse(get(err, 'response.data.message', '{}'));

export const trackLoginSignup = (providerName, account, license) => {
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const isSignUpFlow =
    sessionStorageAccessor.get(SESSION_STORAGE_KEYS.SIGN_UP) === 'true';

  try {
    // Remove the sign-up key after retriving it for future flows
    sessionStorageAccessor.remove(SESSION_STORAGE_KEYS.SIGN_UP);

    // If the account was just created, also track a signup event
    if (isSignUpFlow) {
      const { firstName, lastName } = getFirstAndLastNames(account.name);

      TrackingService.logEvent(TRACKING.EVENTS.SIGN_UP_IDENTIFICATION_OK, {
        identity_email: account.email,
        identity_git_org: account.organization_id,
        identity_first_name: firstName,
        identity_last_name: lastName,
        identity_extra_emails: '',
        method: providerName.replace('_identity', '').toLowerCase(),
        license_plan_name: license === 'team' ? 'free 2.0' : 'trial',
        isSurveyFlow: isSurveyFlow()
      });
    } else {
      // Track a successful login
      TrackingService.logEvent(TRACKING.EVENTS.LOGIN, {
        method: providerName.replace('_identity', '').toLowerCase(),
        isSurveyFlow: isSurveyFlow()
      });
    }
  } catch (e) {
    window.TrackJS && window.TrackJS.track(e);
  }
};

export const isSamlAndTestConnectivity = (
  providerName,
  testConnectivity = 'false'
) => {
  return (
    providerName === IDENTITY_PROVIDERS.SAML && testConnectivity === 'true'
  );
};

export const generateSamlTestConnRedirectUrl = (testPassed = false) => {
  return `${
    AppRouter.SINGLE_SIGN_ON.path
  }?isOrgSettings=true&test_connectivity_res=${
    testPassed ? 'passed' : 'failed'
  }`;
};

export async function postIdentityActions(token, cookieOptions, providerName) {
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const localStorageAccessor = BrowserStorageAccessor().local;
  storeToken(token, cookieOptions);
  let account = null;

  try {
    account = await getAccount();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    window.TrackJS && window.TrackJS.track(e);
    return validateFetchAccountError(e);
  }

  cleanSignInContext(sessionStorageAccessor, localStorageAccessor);

  localStorageAccessor.set(
    LOCAL_STORAGE_KEYS.LAST_PROVIDER_NAME,
    providerName.replace('_identity', '').toLowerCase()
  );
  if (account) {
    localStorageAccessor.set(LOCAL_STORAGE_KEYS.ACCOUNT_ID, account.id);
  }
  if (isGdprCheckboxChecked()) {
    // user approved GDPR privacy-policy on this flow
    accountApprovedGdpr();
  }

  return account;
}

// Function to handle link account flow
export const handleLinkAccountFlow = ({
  accountLinkUrl,
  linkAccountToken,
  history,
  sessionStorageAccessor,
  localStorageAccessor,
  providerName,
  authFlow,
  authLevel
}) => {
  axios
    .get(accountLinkUrl, {
      headers: { 'link-token': linkAccountToken }
    })
    .then(({ token, cookieOptions, account_id }) => {
      if (token) {
        storeToken(token, cookieOptions);
      }
      history.push({
        pathname: '/link-account',
        state: {
          canLogin: !!token,
          accountId: account_id,
          providerName,
          authFlow,
          authLevel
        }
      });
    })
    .catch(err => {
      const error = getError(err);
      history.push({ pathname: '/link-account', state: { error } });
    })
    .finally(() =>
      cleanSignInContext(sessionStorageAccessor, localStorageAccessor)
    );
};

// Function to handle identity verification
export const handleIdentityVerification = ({
  identityUrl,
  providerName,
  testConnectivity,
  history,
  sessionStorageAccessor,
  localStorageAccessor,
  authFlow
}) => {
  axios
    .get(identityUrl, { withCredentials: true })
    .then(
      async ({
        token,
        cookieOptions,
        org_name,
        requestInvitationToken,
        isExistingUser,
        accountEmail,
        organizationName
      }) => {
        const account = await postIdentityActions(
          token,
          cookieOptions,
          providerName
        );

        if (requestInvitationToken) {
          sessionStorageAccessor.set(
            SESSION_STORAGE_KEYS.ORGANIZATION_NAME,
            org_name
          );
          sessionStorageAccessor.set(
            SESSION_STORAGE_KEYS.REQUEST_INVITATION_TOKEN,
            requestInvitationToken
          );
        }

        redirectAfterIdentity(
          history,
          providerName,
          testConnectivity,
          isExistingUser,
          accountEmail,
          organizationName,
          account
        );
      }
    )
    .catch(err => {
      const { type, context } = getError(err);
      TrackingService.logEvent(TRACKING.EVENTS.IDENTIFICATION_FAIL, {
        reason: { type, context, authFlow, isSurveyFlow: isSurveyFlow() }
      });
      history.push({
        pathname: isSamlAndTestConnectivity(providerName, testConnectivity)
          ? generateSamlTestConnRedirectUrl(false)
          : AppRouter.LOGIN.path,
        state: { error: { type, context } }
      });
    })
    .finally(() =>
      cleanSignInContext(sessionStorageAccessor, localStorageAccessor)
    );
};

// Function to handle grant processing
export const handleGrantProcessing = async (grantUrl, history) => {
  const token = getToken();
  if (!token) {
    history.push('/login?autherr=UNKNOWN');
    return;
  }

  try {
    const { redirectTo } = await axios.get(grantUrl, { withCredentials: true });
    history.push(`/${redirectTo}`);
  } catch (error) {
    if (error.response?.status === 409) {
      // conflict: multiple resources
      const redirectTo = error?.response?.data?.redirectTo;
      if (!redirectTo) {
        return history.push('/login?autherr=UNKNOWN');
      }
      const updatedRedirectTo = urlStringHandler(redirectTo)
        .addQuery(WITH_MULTI_SITES_ERROR_QS, 'true')
        .deleteQuery(WITH_SCOPES_VALIDATIONS_QS)
        .toString();

      history.push(`/${updatedRedirectTo}`);
    } else {
      // Handle all other errors
      history.push('/login?autherr=UNKNOWN');
    }
  }
};

// Function to construct constants
export const constructConstants = () => {
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const localStorageAccessor = BrowserStorageAccessor().local;
  const signUpFlow = sessionStorageAccessor.get(SESSION_STORAGE_KEYS.SIGN_UP);

  const search = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true
  });
  if (signUpFlow) {
    search.signUpFlow = signUpFlow;
  }

  const providerName = window.location.pathname.split('/').pop();
  const surveyId = sessionStorageAccessor.get(SESSION_STORAGE_KEYS.SURVEY_ID);
  const invitationId = sessionStorageAccessor.get(
    SESSION_STORAGE_KEYS.INVITATION_ID
  );
  const organizationId = sessionStorageAccessor.get(
    SESSION_STORAGE_KEYS.ORG_ID
  );
  const authFlow = sessionStorageAccessor.get(SESSION_STORAGE_KEYS.AUTH_FLOW);
  const authLevel = sessionStorageAccessor.get(SESSION_STORAGE_KEYS.AUTH_LEVEL);
  const surveyFlow = isSurveyFlow();
  const ssoLoginEmail = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.SSO_LOGIN_EMAIL
  );
  const testConnectivity = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.TEST_CONNECTIVITY
  );
  const linkAccountToken = localStorageAccessor.get(
    LOCAL_STORAGE_KEYS.LINK_ACCOUNT_TOKEN
  );

  const encodedSearch = QueryString.stringify(
    {
      ...search,
      ...(invitationId ? { invitationId } : {}),
      ...(surveyId ? { surveyId } : {}),
      ...(organizationId ? { organizationId } : {}),
      ...(authFlow ? { authFlow } : {}),
      ...(authLevel ? { authLevel } : {}),
      ...(surveyFlow ? { surveyFlow } : {}),
      ...(isSamlAndTestConnectivity(providerName, testConnectivity)
        ? { testConnectivity }
        : {}),
      ...(providerName === IDENTITY_PROVIDERS.SAML ? { ssoLoginEmail } : {})
    },
    { encode: true, strict: true }
  );

  const baseUrl = API_GATEWAY_URI;
  const identityUrl = `${baseUrl}/api/v1/rest/public/auth/identity/success/${providerName}?${encodedSearch}`;
  const grantUrl = `${baseUrl}/api/v1/rest/auth/grant/success/${providerName}?${encodedSearch}`;
  const accountLinkUrl = `${baseUrl}/api/v1/rest/public/auth/link-account/success/${providerName}?${encodedSearch}`;

  return {
    sessionStorageAccessor,
    localStorageAccessor,
    providerName,
    encodedSearch,
    authFlow,
    authLevel,
    linkAccountToken,
    search,
    testConnectivity,
    identityUrl,
    grantUrl,
    accountLinkUrl
  };
};

/**
 * Redirects the user after identity verification based on the provider name, test connectivity status, and user existence.
 * @param {object} history - The history object to navigate through routes.
 * @param {string} providerName - The name of the identity provider.
 * @param {'false' | 'true'} testConnectivity - Flag indicating if test connectivity is enabled.
 * @param {boolean} isExistingUser - Flag indicating if the user already exists.
 * @param {string} accountEmail - The email address associated with the user account.
 * @param {string} [organizationName] - The name of the organization, if available.
 * @param {Record<string, string>} account - User account
 */
export function redirectAfterIdentity(
  history,
  providerName,
  testConnectivity,
  isExistingUser,
  accountEmail,
  organizationName,
  account
) {
  if (isSamlAndTestConnectivity(providerName, testConnectivity)) {
    history.push(generateSamlTestConnRedirectUrl(true));
    return;
  }

  if (isExistingUser) {
    let urlParams = `email=${accountEmail}`;
    if (organizationName) {
      urlParams += `&organization_name=${organizationName}`;
    }

    history.push(`${AppRouter.SIGN_UP.path}?${urlParams}`);
    return;
  }

  const localStorageAccessor = BrowserStorageAccessor().local;
  const license = localStorageAccessor.get(LOCAL_STORAGE_KEYS.LICENSE);

  try {
    trackLoginSignup(providerName, account, license);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }

  history.push(AppRouter.ROOT.path);
}

export const getParsedStateFromQs = history => {
  try {
    const searchProps = QueryString.parse(history.location.search);
    return JSON.parse(atob(searchProps.state));
  } catch (e) {
    return {};
  }
};

const getQsWithoutStateKeys = (history, keys = []) => {
  const parsedState = getParsedStateFromQs(history);
  const stateWithoutKeys = omit(parsedState, keys);
  const encodedNewState = btoa(JSON.stringify(stateWithoutKeys));

  const searchPropsWithUpdatedState = {
    ...QueryString.parse(history.location.search),
    state: encodedNewState
  };

  return QueryString.stringify(searchPropsWithUpdatedState);
};

export const redirectDevClientToLocal = history => {
  const qsWithoutInfiniteLoop = getQsWithoutStateKeys(history, [
    'shouldRedriectToLocalhost'
  ]);

  const localUrl = `http://localhost:3000${history.location.pathname}?${qsWithoutInfiniteLoop}`;
  window.location.href = localUrl;
};
